import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, CheckCircle, Loader2, ShieldCheck, TrendingUp, BarChart3, Users, AlertCircle, Mail, DollarSign, Calendar, UserCheck } from "lucide-react";
import { fadeUp, LuxuryBackground, Section, Card, GoldButton, PageHero } from "../components/shared/UIComponents";
import { API } from "../data/constants";

const WHY_INVEST = [
  { icon: ShieldCheck, title: "Curated Deal Selection", text: "Every opportunity goes through a multi-step review before reaching investors. We evaluate borrower experience, property fundamentals, and market conditions." },
  { icon: TrendingUp, title: "Diversified Exposure", text: "Invest across a portfolio of real estate debt positions — fix & flip, bridge, DSCR, and construction loans — reducing single-asset concentration risk." },
  { icon: BarChart3, title: "Transparent Reporting", text: "Track your portfolio performance, distributions, and fund updates in real time through your private investor dashboard." },
  { icon: Users, title: "Aligned Interests", text: "The Imaani Capital team co-invests alongside our investors. Our returns are tied directly to fund performance." },
];

const PROCESS_STEPS = [
  { title: "Get Accredited", text: "Complete our verification process to confirm accredited investor status. Takes 1-2 business days." },
  { title: "Review Opportunities", text: "Browse curated fund opportunities structured by the Imaani Capital team. Full investment memo provided for each." },
  { title: "Commit Capital", text: "Select your investment amount above the minimum commitment. Complete our electronic subscription agreement." },
  { title: "Track Returns", text: "Monitor your portfolio, distributions, and fund performance in real time through your investor dashboard." },
];

const WHO_INVESTS = [
  "Accredited Individual Investors",
  "Self-Directed IRA / Solo 401(k) Holders",
  "Family Offices",
  "High-Net-Worth Individuals",
  "Real Estate Professionals",
  "Business Owners Seeking Passive Income",
];

export default function CapitalPartnersPage() {
  const [email, setEmail] = useState("");
  const [waitlistStatus, setWaitlistStatus] = useState(null);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setWaitlistLoading(true);
    try {
      await axios.post(`${API}/investor/waitlist`, { email });
      setWaitlistStatus("success");
    } catch {
      setWaitlistStatus("error");
    } finally {
      setWaitlistLoading(false);
    }
  };

  return (
    <div className="relative" data-testid="capital-partners-page">
      <PageHero eyebrow="For Investors" title="Invest in Curated Real Estate Opportunities"
        description="Access institutional-quality real estate debt investments through the Imaani Capital fund platform. Diversified exposure to private lending with quarterly distributions." />
      <div className="relative">
        <LuxuryBackground />

        {/* Why Invest */}
        <Section eyebrow="Why Invest" title="Why Investors Choose Imaani Capital"
          description="We provide accredited investors with access to a curated portfolio of real estate debt positions, backed by transparent reporting and aligned incentives.">
          <div className="grid gap-5 sm:grid-cols-2">
            {WHY_INVEST.map((vp, i) => {
              const Icon = vp.icon;
              return (
                <motion.div key={vp.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                  <Card className="h-full">
                    <div className="w-fit rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 p-3 border border-emerald-200 mb-4">
                      <Icon className="h-5 w-5 text-emerald-700" />
                    </div>
                    <h3 className="text-base font-bold text-[#0A0A0A] mb-2">{vp.title}</h3>
                    <p className="text-sm text-[#555] leading-relaxed">{vp.text}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </Section>

        {/* Yield / Return Highlight Strip */}
        <section className="relative z-10 mx-auto max-w-7xl px-4 pb-6 sm:px-6 sm:pb-10 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="grid gap-4 sm:grid-cols-3" data-testid="yield-highlight-strip">
              {[
                { icon: DollarSign, label: "Target Annual Yield", value: "8% — 12%", color: "text-gold" },
                { icon: Calendar, label: "Distribution Schedule", value: "Quarterly Distributions", color: "text-emerald-600" },
                { icon: UserCheck, label: "Investor Requirement", value: "Accredited Investors Only", color: "text-[#0A0A0A]" },
              ].map((item, i) => (
                <motion.div key={item.label} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <Card hover={false} className="!bg-gradient-to-br !from-emerald-50/80 !to-white !border-emerald-200 text-center">
                    <item.icon className="h-6 w-6 mx-auto mb-3 text-emerald-700" />
                    <div className={`text-xl font-bold ${item.color}`}>{item.value}</div>
                    <div className="text-xs text-[#888] mt-1.5 uppercase tracking-wider">{item.label}</div>
                  </Card>
                </motion.div>
              ))}
            </div>
            {/* Short disclosure below yield strip */}
            <p className="mt-4 text-[11px] text-[#999] text-center leading-relaxed max-w-2xl mx-auto" data-testid="yield-disclosure-short">
              Investment opportunities are available to verified accredited investors only. Target returns are not guaranteed. All investments carry risk of loss.
            </p>
          </motion.div>
        </section>

        {/* How the Investment Process Works */}
        <Section eyebrow="Process" title="How the Investment Process Works">
          <div className="grid gap-5 lg:grid-cols-2">
            {PROCESS_STEPS.map((step, i) => (
              <motion.div key={step.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Card className="flex gap-5 items-start">
                  <motion.div whileHover={{ rotate: 360, scale: 1.1 }} transition={{ duration: 0.5 }}
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-700 to-emerald-900 border-2 border-gold/50 text-lg font-bold text-white">0{i + 1}</motion.div>
                  <div><h3 className="text-xl font-bold text-[#0A0A0A]">{step.title}</h3><p className="mt-3 text-base leading-7 text-[#333]">{step.text}</p></div>
                </Card>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* Who Invests with Imaani Capital */}
        <Section eyebrow="Investors" title="Who Invests with Imaani Capital"
          description="Our fund platform is designed for accredited investors seeking diversified real estate debt exposure with quarterly income.">
          <Card hover={false}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {WHO_INVESTS.map((item, i) => (
                <motion.div key={item} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="rounded-xl border border-cream-warm bg-cream p-4 text-[#333] hover:border-emerald-200 hover:shadow-sm transition-all flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span className="text-sm font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
          </Card>
        </Section>

        {/* Waitlist CTA */}
        <section className="relative z-10 mx-auto max-w-7xl px-4 pb-12 sm:px-6 sm:pb-20 lg:px-8">
          <motion.div {...fadeUp} className="relative rounded-[3rem] overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#022c22] to-[#03352a] rounded-[3rem]" />
            <div className="relative p-10 md:p-16 text-center">
              <p className="text-sm uppercase tracking-[0.4em] text-gold/70 mb-4">Fund I</p>
              <h2 className="text-3xl font-semibold tracking-tight text-cream md:text-5xl luxury-text-gradient">
                Imaani Capital Fund I — Coming Soon
              </h2>
              <p className="mt-6 max-w-2xl mx-auto text-lg leading-8 text-cream-soft">
                We are currently accepting accredited investor applications for our first fund close. Join the waitlist for early access and priority allocation.
              </p>
              <div className="mt-10 max-w-md mx-auto" data-testid="investor-waitlist-form">
                {waitlistStatus === "success" ? (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-center gap-2 py-4 text-sm text-emerald-400 font-medium" data-testid="waitlist-success">
                    <CheckCircle className="h-5 w-5" /> You're on the waitlist. We'll be in touch.
                  </motion.div>
                ) : (
                  <form onSubmit={handleWaitlist} className="flex gap-3">
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                      placeholder="your@email.com" data-testid="waitlist-email-input"
                      className="flex-1 rounded-xl border border-emerald-700/30 bg-emerald-950/60 px-4 py-3.5 text-sm text-cream placeholder:text-cream/30 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/20 transition-colors" />
                    <GoldButton type="submit" disabled={waitlistLoading} data-testid="waitlist-submit-btn" className="shrink-0 !px-6">
                      {waitlistLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="flex items-center gap-2"><Mail className="h-4 w-4" /> Join the Waitlist</span>}
                    </GoldButton>
                  </form>
                )}
                {waitlistStatus === "error" && (
                  <p className="mt-3 text-xs text-red-400 flex items-center justify-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Something went wrong. Please try again.
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </section>

        {/* Full Disclosure */}
        <section className="relative z-10 mx-auto max-w-7xl px-4 pb-16 sm:px-6 sm:pb-28 lg:px-8">
          <div className="rounded-2xl border border-cream-warm bg-cream/60 p-6 md:p-8" data-testid="investor-disclosure">
            <h4 className="text-xs uppercase tracking-wider font-bold text-[#999] mb-3">Important Information</h4>
            <p className="text-xs text-[#888] leading-relaxed">
              The information provided on this page is for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any securities. Investment opportunities are available exclusively to verified accredited investors as defined by SEC Regulation D, Rule 501. Target returns are illustrative projections based on historical performance and market assumptions; they are not guaranteed and actual results may vary materially. All investments in real estate debt carry inherent risks, including the potential loss of principal. Past performance is not indicative of future results. Imaani Capital does not provide tax, legal, or accounting advice. Prospective investors should consult their own professional advisors before making any investment decisions.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
